import React from 'react';
import PropTypes from 'prop-types';
import './Skeleton.scss';

/**
 * SkeletonGroup component - A layout helper for vertical spacing between skeleton blocks.
 *
 * This component provides consistent spacing and layout for skeleton elements,
 * avoiding repeated flex/spacing logic throughout the application. It's particularly
 * useful for creating structured skeleton layouts with proper spacing.
 *
 * @component
 * @example
 * // Basic vertical group
 * <SkeletonGroup spacing="md">
 *   <Skeleton width="100%" height="20px" />
 *   <Skeleton width="80%" height="20px" />
 *   <Skeleton width="60%" height="20px" />
 * </SkeletonGroup>
 *
 * @example
 * // Horizontal group
 * <SkeletonGroup direction="horizontal" spacing="sm">
 *   <Skeleton width="40px" height="40px" borderRadius="50%" />
 *   <Skeleton width="120px" height="20px" />
 * </SkeletonGroup>
 *
 * @example
 * // Grid layout
 * <SkeletonGroup direction="grid" gridColumns={3} spacing="lg">
 *   <Skeleton width="100%" height="120px" />
 *   <Skeleton width="100%" height="120px" />
 *   <Skeleton width="100%" height="120px" />
 * </SkeletonGroup>
 */
const SkeletonGroup = ({
  children,
  spacing = 'md',
  direction = 'vertical',
  gridColumns,
  className = '',
  style = {},
  ...props
}) => {
  const gridStyle = gridColumns
    ? {
        display: 'grid',
        gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
        gap: `var(--spacing-${spacing})`,
      }
    : {
        display: 'flex',
        flexDirection: direction === 'horizontal' ? 'row' : 'column',
        gap: `var(--spacing-${spacing})`,
        alignItems: direction === 'horizontal' ? 'center' : 'stretch',
      };

  return (
    <div
      className={`skeleton-group skeleton-group--${direction} ${className}`}
      style={{
        ...gridStyle,
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
};

SkeletonGroup.propTypes = {
  /** Skeleton elements to group */
  children: PropTypes.node.isRequired,

  /** Spacing between skeleton elements (xs, sm, md, lg, xl) */
  spacing: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),

  /** Layout direction (vertical, horizontal, grid) */
  direction: PropTypes.oneOf(['vertical', 'horizontal', 'grid']),

  /** Number of columns when direction is 'grid' */
  gridColumns: PropTypes.number,

  /** Additional CSS classes */
  className: PropTypes.string,

  /** Additional inline styles */
  style: PropTypes.object,
};

export default SkeletonGroup;
